/**
 * VÉRAULT Portal — Portal Access Card (Wave 35 · Phase 1 · PI-2)
 *
 * The advisor's one-stop control for a client's portal access:
 *   · Sees current state (linked email · last login · access level · expiry)
 *   · Grants new access (creates auth + temporary password)
 *   · Rotates the temporary password
 *   · Sets access level + expiry
 *   · Enables / disables
 *
 * Feb 2026 — Antonia requested ALWAYS-visible temp passwords for her own
 * clients. The advisor side now displays the current temp password
 * (with reveal / copy) and, after a rotate, the previous password too —
 * never expires, never re-hashed-only. This is an advisor-only surface
 * gated behind the admin auth.
 */
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  KeyRound,
  RotateCw,
  Copy,
  Check,
  Loader2,
  Eye,
  EyeOff,
  ShieldAlert,
  Clock,
  UserCheck,
  UserX,
  History,
} from "lucide-react";
import {
  Surface,
  PortalButton,
  StatusTag,
  EditorialRule,
  AIWhisper,
  api,
} from "../../../index";
import { toast } from "sonner";

const ACCESS_LEVELS = [
  { value: "primary_client", label: "Primary Client" },
  { value: "trip_companion", label: "Trip Companion" },
  { value: "viewer", label: "Viewer" },
];

const EXPIRY_PRESETS = [
  { value: "", label: "No expiry" },
  { value: "7", label: "7 days" },
  { value: "14", label: "14 days" },
  { value: "30", label: "30 days" },
  { value: "60", label: "60 days" },
  { value: "90", label: "90 days" },
];

function formatDateTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function CurrentPasswordPanel({ current, previous, previousIssuedAt, issuedAt, justRotated }) {
  const [shown, setShown] = useState(Boolean(justRotated));
  const [showPrev, setShowPrev] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — please write it down.");
    }
  };

  if (!current) {
    return (
      <Surface variant="paper" padded className="!p-4" data-testid="portal-access-current-password-empty">
        <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
          No stored password yet — rotate to generate one.
        </p>
      </Surface>
    );
  }

  return (
    <Surface
      variant="paper"
      padded
      className="!p-4 border-[hsl(var(--accent-gold))]/40"
      data-testid="portal-access-current-password"
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center size-9 rounded-full bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] shrink-0">
          <KeyRound className="size-4" strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
            {justRotated ? "New password · just generated" : "Current password"}
            {issuedAt ? (
              <span className="ml-2 normal-case tracking-normal text-[10.5px] italic font-accent">
                · issued {formatDateTime(issuedAt) || ""}
              </span>
            ) : null}
          </p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <code
              className="px-3 py-1.5 rounded bg-[hsl(var(--background-paper))] font-mono text-[15px] tracking-wider select-all"
              data-testid="portal-access-current-password-value"
            >
              {shown ? current : "•".repeat(current.length)}
            </code>
            <PortalButton
              variant="ghost"
              size="sm"
              onClick={() => setShown((v) => !v)}
              data-testid="portal-access-current-toggle-show"
            >
              {shown ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
              {shown ? "Hide" : "Show"}
            </PortalButton>
            <PortalButton
              variant="ghost"
              size="sm"
              onClick={() => copy(current)}
              data-testid="portal-access-current-copy"
            >
              {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
              {copied ? "Copied" : "Copy"}
            </PortalButton>
          </div>
          <p className="mt-2 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
            This is the password the client uses to sign in right now. Stored in your advisor view only — never visible to anyone else.
          </p>

          {previous && (
            <div className="mt-3 pt-3 border-t border-[hsl(var(--surface-hairline))]" data-testid="portal-access-previous-password">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] flex items-center gap-1.5">
                <History className="size-3 opacity-70" />
                Previous password · no longer works
                {previousIssuedAt ? (
                  <span className="normal-case tracking-normal text-[10.5px] italic font-accent ml-1">
                    · issued {formatDateTime(previousIssuedAt) || ""}
                  </span>
                ) : null}
              </p>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <code
                  className="px-3 py-1 rounded bg-[hsl(var(--background-paper))] font-mono text-[13px] tracking-wider select-all opacity-80"
                  data-testid="portal-access-previous-password-value"
                >
                  {showPrev ? previous : "•".repeat(previous.length)}
                </code>
                <PortalButton
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPrev((v) => !v)}
                  data-testid="portal-access-previous-toggle-show"
                >
                  {showPrev ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                  {showPrev ? "Hide" : "Show"}
                </PortalButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </Surface>
  );
}

function TempPasswordReveal({ password, onClose }) {
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — please write it down.");
    }
  };

  return (
    <Surface
      variant="paper"
      padded
      className="!p-4 border-[hsl(var(--accent-gold))]/40"
      data-testid="portal-access-temp-password"
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center size-9 rounded-full bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] shrink-0">
          <ShieldAlert className="size-4" strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
            Temporary password · show once
          </p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <code
              className="px-3 py-1.5 rounded bg-[hsl(var(--background-paper))] font-mono text-[15px] tracking-wider select-all"
              data-testid="portal-access-temp-password-value"
            >
              {shown ? password : "•".repeat(password.length)}
            </code>
            <PortalButton
              variant="ghost"
              size="sm"
              onClick={() => setShown((v) => !v)}
              data-testid="portal-access-toggle-show"
            >
              {shown ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
              {shown ? "Hide" : "Show"}
            </PortalButton>
            <PortalButton
              variant="ghost"
              size="sm"
              onClick={copy}
              data-testid="portal-access-copy"
            >
              {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
              {copied ? "Copied" : "Copy"}
            </PortalButton>
          </div>
          <p className="mt-2 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
            Copy it now — once you dismiss, it can&apos;t be shown again. Rotate to generate a new one.
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-[12px] font-mono uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
          data-testid="portal-access-temp-password-dismiss"
        >
          Dismiss
        </button>
      </div>
    </Surface>
  );
}

export default function PortalAccessCard({ clientId, defaultEmail, clientName }) {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [emailInput, setEmailInput] = useState(defaultEmail || "");
  const [expiryDays, setExpiryDays] = useState("");
  const [accessLevel, setAccessLevel] = useState("primary_client");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/clients/${clientId}/portal-access`);
      setState(data);
      setEmailInput(data?.access?.email || data?.client_email || defaultEmail || "");
      setAccessLevel(data?.access?.access_level || "primary_client");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load portal access");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) load();
  }, [clientId]);

  const access = state?.access;
  const hasAccess = Boolean(access);
  const isActive = Boolean(access?.is_active);

  const grant = async () => {
    if (!emailInput) {
      toast.error("Email required to grant access");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/clients/${clientId}/portal-access/grant`, {
        email: emailInput,
        access_level: accessLevel,
        expires_in_days: expiryDays ? Number(expiryDays) : null,
      });
      setTempPassword(data.temp_password);
      setState({ ...state, access: data.access, client_email: emailInput });
      toast.success(hasAccess ? "Password rotated" : "Portal access granted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't grant access");
    } finally {
      setBusy(false);
    }
  };

  const rotate = async () => {
    if (!window.confirm("Generate a brand-new temporary password? The old one stops working immediately.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/clients/${clientId}/portal-access/rotate`);
      setTempPassword(data.temp_password);
      setState({ ...state, access: data.access });
      toast.success("Password rotated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't rotate password");
    } finally {
      setBusy(false);
    }
  };

  const setLevel = async (newLevel) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/clients/${clientId}/portal-access`, {
        access_level: newLevel,
      });
      setState({ ...state, access: data.access });
      setAccessLevel(newLevel);
      toast.success("Access level updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    } finally {
      setBusy(false);
    }
  };

  const setExpiry = async (days) => {
    setBusy(true);
    try {
      const payload = days === "" ? { expires_in_days: -1 } : { expires_in_days: Number(days) };
      const { data } = await api.patch(`/admin/clients/${clientId}/portal-access`, payload);
      setState({ ...state, access: data.access });
      setExpiryDays(days);
      toast.success(days === "" ? "Expiry cleared" : `Expires in ${days} days`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    } finally {
      setBusy(false);
    }
  };

  const toggleEnable = async () => {
    setBusy(true);
    try {
      const path = isActive ? "disable" : "enable";
      const { data } = await api.post(`/admin/clients/${clientId}/portal-access/${path}`);
      setState({ ...state, access: data.access });
      toast.success(isActive ? "Access disabled" : "Access enabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Surface variant="paper" padded data-testid="portal-access-loading">
        <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] flex items-center gap-2">
          <Loader2 className="size-3.5 animate-spin" /> Loading portal access…
        </p>
      </Surface>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-4"
      data-testid="portal-access-card"
    >
      <Surface variant="paper" padded>
        <div className="flex items-start gap-3 flex-wrap">
          <span className="inline-flex items-center justify-center size-9 rounded-full bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground-muted))] shrink-0">
            <KeyRound className="size-4" strokeWidth={1.5} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              Portal Access
            </p>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              {hasAccess ? (
                <>
                  <p className="font-serif text-[17px]">{access.email}</p>
                  <StatusTag tone={isActive ? "success" : "muted"}>
                    {isActive ? "Active" : "Disabled"}
                  </StatusTag>
                  <StatusTag tone="neutral">
                    {ACCESS_LEVELS.find((l) => l.value === access.access_level)?.label || access.access_level}
                  </StatusTag>
                </>
              ) : (
                <p className="font-serif text-[16px] text-[hsl(var(--foreground-muted))]">
                  No portal account yet.
                </p>
              )}
            </div>
            {hasAccess && (
              <div className="mt-2 grid sm:grid-cols-2 gap-1 text-[12.5px] text-[hsl(var(--foreground-muted))]">
                <div className="flex items-center gap-1.5">
                  <UserCheck className="size-3 opacity-70" />
                  Last login: {formatDateTime(access.last_login_at) || "—"}
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="size-3 opacity-70" />
                  Expires: {formatDateTime(access.access_expires_at) || "no expiry"}
                </div>
              </div>
            )}
          </div>
        </div>
      </Surface>

      {tempPassword && (
        <CurrentPasswordPanel
          current={tempPassword}
          previous={state?.access?.temp_password_previous}
          previousIssuedAt={state?.access?.temp_password_previous_issued_at}
          issuedAt={state?.access?.temp_password_issued_at}
          justRotated
        />
      )}
      {!tempPassword && hasAccess && state?.access?.temp_password_current && (
        <CurrentPasswordPanel
          current={state.access.temp_password_current}
          previous={state.access.temp_password_previous}
          previousIssuedAt={state.access.temp_password_previous_issued_at}
          issuedAt={state.access.temp_password_issued_at}
        />
      )}

      <Surface variant="default" padded>
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-3">
          {hasAccess ? "Manage access" : "Grant access"}
        </p>

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
              Login email
            </span>
            <input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              disabled={hasAccess}
              className="mt-1 w-full px-3 py-2 bg-[hsl(var(--background-paper))] border border-[hsl(var(--surface-hairline))] rounded font-mono text-[13px] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent-gold))] disabled:opacity-60"
              data-testid="portal-access-email-input"
            />
          </label>

          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
              Access level
            </span>
            <select
              value={accessLevel}
              onChange={(e) => (hasAccess ? setLevel(e.target.value) : setAccessLevel(e.target.value))}
              disabled={busy}
              className="mt-1 w-full px-3 py-2 bg-[hsl(var(--background-paper))] border border-[hsl(var(--surface-hairline))] rounded font-mono text-[13px] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent-gold))]"
              data-testid="portal-access-level-select"
            >
              {ACCESS_LEVELS.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
              Expiry
            </span>
            <select
              value={expiryDays}
              onChange={(e) => (hasAccess ? setExpiry(e.target.value) : setExpiryDays(e.target.value))}
              disabled={busy}
              className="mt-1 w-full px-3 py-2 bg-[hsl(var(--background-paper))] border border-[hsl(var(--surface-hairline))] rounded font-mono text-[13px] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent-gold))]"
              data-testid="portal-access-expiry-select"
            >
              {EXPIRY_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {!hasAccess ? (
            <PortalButton
              variant="primary"
              size="sm"
              onClick={grant}
              disabled={busy || !emailInput}
              data-testid="portal-access-grant"
            >
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : <KeyRound className="size-3.5" />}
              Grant access
            </PortalButton>
          ) : (
            <>
              <PortalButton
                variant="primary"
                size="sm"
                onClick={rotate}
                disabled={busy}
                data-testid="portal-access-rotate"
              >
                {busy ? <Loader2 className="size-3.5 animate-spin" /> : <RotateCw className="size-3.5" />}
                Rotate password
              </PortalButton>
              <PortalButton
                variant="ghost"
                size="sm"
                onClick={toggleEnable}
                disabled={busy}
                data-testid="portal-access-toggle"
              >
                {isActive ? <UserX className="size-3.5" /> : <UserCheck className="size-3.5" />}
                {isActive ? "Disable access" : "Enable access"}
              </PortalButton>
            </>
          )}
        </div>

        <EditorialRule className="my-4" />
        <AIWhisper label="A quiet note" testid="portal-access-note">
          {clientName || "Your client"} receives one permanent login, even across multiple trips.
          Temporary passwords follow the VÉRAULT format <span className="font-mono">[Key][Year][Letter][Number][Symbol]</span> &mdash; e.g.&nbsp;
          <span className="font-mono">Jxn26M7!</span>. The current password is stored in your advisor view so you can read it back at any time, and the previous password is kept for reference until the next rotation.
        </AIWhisper>
      </Surface>
    </motion.div>
  );
}
